import { computed } from "vue";
import { useQuery } from "@tanstack/vue-query";

import { getMe } from "@/api/client";

// Roles permitted to approve / reject / reopen submitted records. Must match
// the server's _APPROVER_CAPABLE_ROLES (api/sync.py) and the mobile app's
// approverRoles (core/auth/me_provider.dart), so web, mobile and server all
// agree on who can approve — the web previously showed Approve to every
// non-client, including roles the server rejects.
export const APPROVER_ROLES = [
  "cre",
  "project_manager",
  "office",
  "manager",
  "admin",
];

// Management roles (matches the server's MANAGEMENT_ROLES, auth/deps.py) —
// project + user administration.
export const MANAGEMENT_ROLES = ["office", "manager", "admin", "project_manager"];

/**
 * Current-user permission flags, read from `/me`. The single place the web
 * decides role-based access (previously an ad-hoc `roles.includes("client")`
 * repeated per view).
 */
export function usePermissions() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const roles = computed(() => me.value?.roles ?? []);
  const isClient = computed(() => roles.value.includes("client"));
  // Approvers must be non-client AND hold an approver role.
  const canApprove = computed(
    () =>
      !isClient.value && roles.value.some((r) => APPROVER_ROLES.includes(r)),
  );
  // Project + user administration (matches the server's MANAGEMENT_ROLES).
  const isManagement = computed(() =>
    roles.value.some((r) => MANAGEMENT_ROLES.includes(r)),
  );
  return { me, roles, isClient, canApprove, isManagement };
}
